import { filter, includes, isEmpty, map, mapObjIndexed, toLower } from 'ramda';
import React, { ChangeEventHandler, FC, ReactNode, forwardRef, useMemo, useState } from 'react';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';

import GroupedList from '@ui/forms/GroupedList';

import SearchInput, { ISearchInputProps } from '@ui/molecules/SearchInput/SearchInput.component';

import { doesNotExist, exists } from '@utils/general';
import { convertToOrderedList } from '@utils/orderedList';

import { IListItem, IListItems, IOrderedList } from '@folx-types/data';
import { SpacingTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface ISearchableListProps extends ISearchInputProps {
	data: IOrderedList;
	name: string;
	onChange: ChangeEventHandler<HTMLInputElement>;
	searchLabel: string;
	value?: string;
	defaultOption?: IListItem | null;
	searchName?: string;
	searchPosition?: SpacingTypes;
}

const StickySearch = styled.div<{ searchPosition: SpacingTypes }>`
	position: sticky;
	top: ${({ searchPosition }) => theme.spacing[searchPosition]};
	background-color: ${theme.colors.white};
	z-index: ${theme.zIndexes.foreground};
`;

const SearchableList: FC<ISearchableListProps> = forwardRef<HTMLInputElement, ISearchableListProps>(
	(
		{
			data,
			defaultOption,
			onChange,
			name,
			searchLabel,
			searchName = 'search',
			searchPosition = 'none',
			value,
			...searchProps
		},
		ref
	) => {
		const [searchValue, setSearchValue] = useState('');

		const filteredData: IOrderedList = useMemo(() => {
			if (doesNotExist(searchValue)) {
				return data;
			}

			const searchResults = mapObjIndexed((list) => {
				const filteredList = filter(
					(item) => includes(toLower(searchValue), toLower(item.label)),
					list
				);
				return filteredList;
			}, data);

			const filteredResults = filter((list) => exists(list), searchResults);
			return isEmpty(filteredResults) && exists(defaultOption)
				? {
						...filteredResults,
						...convertToOrderedList([defaultOption]),
				  }
				: filteredResults;
		}, [searchValue]);

		return (
			<FlexColumn gap="sm">
				<StickySearch searchPosition={searchPosition}>
					<SearchInput
						data-testid="SearchInput"
						{...searchProps}
						name={searchName}
						label={searchLabel}
						value={searchValue}
						labelVariant="field"
						clear={() => {
							setSearchValue('');
						}}
						onChange={(event) => {
							setSearchValue(event.target.value);
						}}
					/>
				</StickySearch>
				{map<[string, IListItems], ReactNode>(([header, list]) => {
					return (
						<GroupedList
							ref={ref}
							key={header}
							header={header}
							groupName={name}
							listItems={list}
							value={value}
							onChange={onChange}
						/>
					);
				}, Object.entries(filteredData))}
			</FlexColumn>
		);
	}
);

export default styled(SearchableList)``;
SearchableList.displayName = 'SearchableList';
